/**
 * Minimal SAFE renderer for chat messages: the model's markdown-ish output is
 * HTML-escaped first, then only two constructs are re-introduced — **bold**
 * and [label](https://…) links. Everything else stays literal text (rendered
 * with `white-space: pre-line`, so newlines survive without <br>). This
 * replaces the legacy widget's raw `dangerouslySetInnerHTML` of backend text.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * SAME-SITE action link: `[label](/en/demo)`. Rendered as an in-app anchor
 * (no target=_blank) carrying the `chat-cta` class, which the widget styles as
 * an orange action button — the visitor stays on this domain instead of being
 * thrown onto app.omceanbooking.com in a new tab.
 *
 * SECURITY: exactly one leading slash and then a path character. `//evil.com`
 * is protocol-relative and would leave the site, so it must NOT match, and
 * neither may `javascript:`/`data:` (they have no leading slash).
 */
const SAME_SITE_LINK = /\[([^\]\n]+)\]\((\/(?!\/)[A-Za-z0-9._~\-/?#=&%]*)\)/g;
const SAME_SITE_REPLACEMENT = '<a href="$2" class="chat-cta">$1</a>';

/** Historical assistant replies used the app's demo URL. The marketing demo
 * belongs to this site; normalize this one known destination before escaping
 * and rendering so even retained/raw replies produce the correct action. */
function normalizeDemoLinks(text: string): string {
  const chinese = /[㐀-鿿]/u.test(text);
  const path = chinese ? '/zh/demo' : '/en/demo';
  const label = chinese ? '看即時示範' : 'See the live demo';
  return text
    .replace(
      /\[([^\]\n]+)\]\((https?:\/\/app\.omceanbooking\.com\/(?:en\/|cn\/|zh\/)?demo\/?|\/cn\/demo\/?)\)/gi,
      (_match, title: string, url: string) =>
        `[${title}](${url.startsWith('/cn/') ? '/zh/demo' : path})`
    )
    .replace(
      /(?<![\w/])https?:\/\/app\.omceanbooking\.com\/(?:en\/|cn\/|zh\/)?demo\/?(?=$|[\s<>()"'，。！？、])/gi,
      `[${label}](${path})`
    );
}

export function renderChatMarkdown(text: string): string {
  let html = escapeHtml(normalizeDemoLinks(text));
  // [label](https://url) — http(s) only, so javascript:/data: can never link.
  html = html.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  html = html.replace(SAME_SITE_LINK, SAME_SITE_REPLACEMENT);
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  return html;
}

function renderInlineMarkdown(text: string): string {
  let html = escapeHtml(text);
  html = html.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  html = html.replace(SAME_SITE_LINK, SAME_SITE_REPLACEMENT);
  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  return html;
}

function isMarkdownTableSeparator(line: string): boolean {
  const cells = line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());
  return cells.length >= 2 && cells.every(cell => /^:?-{3,}:?$/.test(cell));
}

function parseMarkdownTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());
}

function renderMarkdownTable(headers: string[], rows: string[][]): string {
  const width = headers.length;
  const cells = (values: string[], tag: 'th' | 'td') =>
    Array.from({ length: width }, (_, index) => {
      const value = values[index] ?? '';
      return `<${tag}>${renderInlineMarkdown(value)}</${tag}>`;
    }).join('');

  return (
    '<div class="ob-md-table-wrap"><table><thead><tr>' +
    cells(headers, 'th') +
    '</tr></thead><tbody>' +
    rows.map(row => `<tr>${cells(row, 'td')}</tr>`).join('') +
    '</tbody></table></div>'
  );
}

/** Safe block renderer for full assistant responses. It supports the small
 * Markdown subset the model commonly emits while escaping all source text
 * before introducing known tags. */
export function renderChatMarkdownBlocks(text: string): string {
  const lines = normalizeDemoLinks(text).replace(/\r\n/g, '\n').split('\n');
  const output: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  let paragraph: string[] = [];
  const closeParagraph = () => {
    if (paragraph.length === 0) return;
    output.push(`<p>${paragraph.map(renderInlineMarkdown).join('<br>')}</p>`);
    paragraph = [];
  };
  const closeList = () => {
    if (!list) return;
    output.push(`</${list}>`);
    list = null;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index];
    const line = rawLine.trim();
    if (!line) {
      closeParagraph();
      closeList();
      continue;
    }
    const nextLine = lines[index + 1]?.trim() ?? '';
    if (line.includes('|') && isMarkdownTableSeparator(nextLine)) {
      closeParagraph();
      closeList();
      const headers = parseMarkdownTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length) {
        const row = lines[index].trim();
        if (!row || !row.includes('|')) break;
        rows.push(parseMarkdownTableRow(row));
        index += 1;
      }
      index -= 1;
      output.push(renderMarkdownTable(headers, rows));
      continue;
    }
    const unordered = line.match(/^[-*]\s+(.+)$/);
    const ordered = line.match(/^\d+[.)]\s+(.+)$/);
    if (unordered || ordered) {
      closeParagraph();
      const nextList = unordered ? 'ul' : 'ol';
      if (list !== nextList) {
        closeList();
        list = nextList;
        output.push(`<${list}>`);
      }
      output.push(
        `<li>${renderInlineMarkdown((unordered || ordered)?.[1] ?? '')}</li>`
      );
      continue;
    }
    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      closeParagraph();
      closeList();
      const level = heading[1].length + 2;
      output.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
      continue;
    }
    closeList();
    paragraph.push(line);
  }
  closeParagraph();
  closeList();
  return output.join('');
}
